import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CalculatorCountry,
  CalculatorFormSchema,
  CalculatorVariant,
  VitaCalculatorService,
} from '../../../../services/vita-calculator.service';
import { flagAssetFor } from '../../../../utils/beneficiario-internacional.util';

export interface SelectDestinoDialogData {
  /** Países disponibles — el contenedor ya los filtró por rail de envío. */
  countries: CalculatorCountry[];
}

/** Destino elegido, con el schema del formulario ya resuelto. */
export interface SelectDestinoResult {
  countryCode: string;
  variant: string;
  variantLabel: string;
  schema: CalculatorFormSchema;
}

/**
 * Paso previo al alta de un beneficiario: país y método de pago.
 *
 * El formulario del beneficiario es dinámico y depende del método elegido, así
 * que no se puede pedir ningún dato antes de saberlo. Este diálogo resuelve esa
 * dependencia y devuelve el schema ya cargado, para que el contenedor solo
 * tenga que abrir el formulario.
 */
@Component({
  selector: 'app-select-destino-dialog',
  templateUrl: './select-destino-dialog.component.html',
  styleUrls: ['./select-destino-dialog.component.sass'],
})
export class SelectDestinoDialogComponent implements OnInit {
  countryCode = '';

  variants: CalculatorVariant[] = [];
  variantsLoading = false;
  variantsError: string | null = null;
  variantCode = '';

  /** Carga del schema al confirmar (último paso antes de cerrar). */
  loadingSchema = false;
  schemaError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<
      SelectDestinoDialogComponent,
      SelectDestinoResult | null
    >,
    @Inject(MAT_DIALOG_DATA) public data: SelectDestinoDialogData,
    private calc: VitaCalculatorService,
  ) {}

  ngOnInit(): void {
    // Un solo destino disponible → preseleccionarlo y ahorrarle el click.
    if (this.data.countries.length === 1) {
      this.selectCountry(this.data.countries[0].code);
    }
  }

  flagFor(code: string): string | null {
    return flagAssetFor(code);
  }

  selectCountry(code: string): void {
    if (this.countryCode === code) return;
    this.countryCode = code;
    this.variantCode = '';
    this.variants = [];
    this.schemaError = null;
    this.loadVariants(code);
  }

  private loadVariants(code: string): void {
    this.variantsLoading = true;
    this.variantsError = null;
    this.calc.getVariants(code).subscribe({
      next: (list) => {
        // Perú: se descartan las variantes USD, igual que en el envío. El
        // destinatario cobra en PEN; ofrecer USD acá termina en un método que
        // después no se puede usar.
        this.variants =
          code.toUpperCase() === 'PE'
            ? (list || []).filter((v) => !/usd/i.test(v.code || ''))
            : list || [];
        this.variantsLoading = false;
        if (this.variants.length === 1) {
          this.variantCode = this.variants[0].code;
        }
      },
      error: (err) => {
        this.variantsLoading = false;
        this.variantsError =
          typeof err === 'string' ? err : 'No pudimos cargar los métodos de pago.';
      },
    });
  }

  get selectedVariant(): CalculatorVariant | null {
    return this.variants.find((v) => v.code === this.variantCode) || null;
  }

  get puedeContinuar(): boolean {
    return !!this.countryCode && !!this.variantCode && !this.loadingSchema;
  }

  continuar(): void {
    if (!this.puedeContinuar) return;
    const variant = this.selectedVariant;
    if (!variant) return;

    this.loadingSchema = true;
    this.schemaError = null;
    this.calc.getRules(variant.code).subscribe({
      next: (schema) => {
        this.loadingSchema = false;
        this.dialogRef.close({
          countryCode: this.countryCode,
          variant: variant.code,
          variantLabel: variant.label || variant.code,
          schema,
        });
      },
      error: (err) => {
        this.loadingSchema = false;
        this.schemaError =
          typeof err === 'string'
            ? err
            : 'No pudimos cargar el formulario de este método. Intentá de nuevo.';
      },
    });
  }

  cerrar(): void {
    this.dialogRef.close(null);
  }
}
